import React, { useState, useEffect, useRef } from "react";
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaUser, FaCheckCircle, FaTimesCircle, FaPaperPlane, FaClock, FaLinkedin, FaGithub } from "react-icons/fa";
import { FiChevronRight, FiSend } from "react-icons/fi";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    phone: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modal, setModal] = useState({ open: false, success: true, message: "" });
  const [errors, setErrors] = useState({});
  const sectionRef = useRef(null);
  const formRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.subject.trim()) newErrors.subject = "Subject is required";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    if (formData.phone && !/^[0-9+\-\s()]{10,}$/.test(formData.phone)) {
      newErrors.phone = "Phone number is invalid";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key]) {
        formDataToSend.append(key, formData[key]);
      }
    });

    try {
      const response = await fetch("https://formspree.io/f/xldqyeqg", {
        method: "POST",
        body: formDataToSend,
        headers: { Accept: "application/json" },
      });

      if (response.ok) {
        setModal({
          open: true,
          success: true,
          message: "Message sent successfully! We'll get back to you within 24 hours. 🚀"
        });
        setFormData({ name: "", email: "", subject: "", message: "", phone: "" });
        setErrors({});
      } else {
        throw new Error("Failed to send message");
      }
    } catch (error) {
      setModal({
        open: true,
        success: false,
        message: "Failed to send message. Please try again or contact us directly via email."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setModal({ ...modal, open: false });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="contact" className="contact" ref={sectionRef}>
      {/* Background Elements */}
      <div className="background-elements">
        <div className="contact-circle circle-1"></div>
        <div className="contact-circle circle-2"></div>
        <div className="contact-circle circle-3"></div>
      </div>

      <div className="container">
        <div className="section-header">
          <div className="section-badge">
            <span className="badge-dot"></span>
            <span className="badge-text">Get In Touch</span>
          </div>
          
          <h1 className="section-title">
            <span className="title-line title-line-1">Let's Build</span>
            <span className="title-line title-line-2">
              <span className="gradient-text">Something Amazing</span>
            </span>
            <span className="title-line title-line-3">Together</span>
          </h1>

          <p className="section-description">
            Have a project in mind? Let's discuss how we can help bring your ideas to life. 
            Reach out to us for a free consultation.
          </p>
        </div>

        <div className="contact-grid">
          {/* Contact Information */}
          <div className="contact-info">
            <div className="info-section">
              <h3 className="info-title">Contact Information</h3>
              <p className="info-subtitle">
                Feel free to reach out using any of the following methods.
              </p>
              
              <div className="info-cards">
                <div className="info-card">
                  <div className="card-icon">
                    <FaUser />
                  </div>
                  <div className="card-content">
                    <h4>Company</h4>
                    <p>Codriva Tech</p>
                    <span className="card-tag">IT Solutions Company</span>
                  </div>
                </div>

                <div className="info-card">
                  <div className="card-icon">
                    <FaPhone />
                  </div>
                  <div className="card-content">
                    <h4>Phone</h4>
                    <a href="tel:+919301542085" className="contact-link">
                      +91 9301542085
                    </a>
                    <span className="card-hint">Mon-Fri, 9AM-6PM</span>
                  </div>
                </div>

                <div className="info-card">
                  <div className="card-icon">
                    <FaEnvelope />
                  </div>
                  <div className="card-content">
                    <h4>Email</h4>
                    <a href="mailto:codrivatech.contact@gmail.com" className="contact-link">
                      codrivatech.contact@gmail.com
                    </a>
                    <span className="card-hint">Response within 24 hours</span>
                  </div>
                </div>

                <div className="info-card">
                  <div className="card-icon">
                    <FaMapMarkerAlt />
                  </div>
                  <div className="card-content">
                    <h4>Location</h4>
                    <p>Gwalior Morar</p>
                    <span className="card-tag">Madhya Pradesh, India</span>
                  </div>
                </div>
              </div>
            </div>

            {/* <div className="social-section">
              <h4>Follow Us</h4>
              <div className="social-links">
                <a href="#" className="social-link" aria-label="LinkedIn">
                  <FaLinkedin />
                </a>
                <a href="#" className="social-link" aria-label="GitHub">
                  <FaGithub />
                </a>
                <a href="#" className="social-link" aria-label="Email">
                  <FaEnvelope />
                </a>
              </div>
            </div> */}
          </div>

          {/* Contact Form */}
          <div className="contact-form-section">
            <div className="form-header">
              <h3>
                <FaPaperPlane className="header-icon" />
                Send us a Message
              </h3>
              <p>Fill out the form below and we'll get back to you promptly.</p>
            </div>

            <form ref={formRef} onSubmit={handleSubmit} className="contact-form">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="name">
                    Full Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className={errors.name ? "error" : ""}
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    Email Address <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className={errors.email ? "error" : ""}
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 9876543210"
                    className={errors.phone ? "error" : ""}
                  />
                  {errors.phone && <span className="error-message">{errors.phone}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="subject">
                    Subject <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Project Inquiry"
                    className={errors.subject ? "error" : ""}
                  />
                  {errors.subject && <span className="error-message">{errors.subject}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">
                  Your Message <span className="required">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your project, requirements, timeline, and budget..."
                  rows="6"
                  className={errors.message ? "error" : ""}
                />
                {errors.message && <span className="error-message">{errors.message}</span>}
              </div>

              <div className="form-footer">
                <div className="form-note">
                  <FaClock className="note-icon" />
                  <span>We typically respond within 24 hours</span>
                </div>
                
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner"></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <FiSend className="button-icon" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Modal */}
        {modal.open && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className={`modal-icon ${modal.success ? 'success' : 'error'}`}>
                {modal.success ? <FaCheckCircle /> : <FaTimesCircle />}
              </div>
              <h3>{modal.success ? 'Success!' : 'Oops!'}</h3>
              <p>{modal.message}</p>
              <button onClick={closeModal} className="modal-button">
                {modal.success ? 'Continue' : 'Try Again'}
                <FiChevronRight className="button-icon" />
              </button>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .contact {
          padding: 160px 0;
          background: linear-gradient(135deg, 
            #020617 0%,
            #0f172a 30%,
            #1e293b 70%,
            #0f172a 100%);
          color: #f8fafc;
          position: relative;
          overflow: hidden;
        }

        /* Background Elements */
        .background-elements {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
        }

        .contact-circle {
          position: absolute;
          border-radius: 50%;
          border: 1px solid rgba(34, 211, 238, 0.1);
          animation: float 12s ease-in-out infinite;
        }

        .circle-1 {
          width: 400px;
          height: 400px;
          top: -200px;
          right: -100px;
          animation-delay: 0s;
        }

        .circle-2 {
          width: 300px;
          height: 300px;
          bottom: -150px;
          left: -100px;
          animation-delay: -4s;
          animation-duration: 15s;
        }

        .circle-3 {
          width: 200px;
          height: 200px;
          top: 50%;
          left: 10%;
          animation-delay: -8s;
          animation-duration: 18s;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 32px;
          position: relative;
          z-index: 2;
        }

        /* Section Header */
        .section-header {
          text-align: center;
          margin-bottom: 80px;
        }

        .section-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(34, 211, 238, 0.1);
          padding: 10px 24px;
          border-radius: 50px;
          margin-bottom: 32px;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.8s ease, transform 0.8s ease;
        }

        .contact.in-view .section-badge {
          opacity: 1;
          transform: translateY(0);
        }

        .badge-dot {
          width: 8px;
          height: 8px;
          background: #22d3ee;
          border-radius: 50%;
          animation: pulse 2s ease-in-out infinite;
        }

        .badge-text {
          font-size: 0.9rem;
          font-weight: 600;
          color: #94a3b8;
          letter-spacing: 1px;
        }

        .section-title {
          font-size: 4rem;
          font-weight: 800;
          line-height: 1.1;
          margin-bottom: 24px;
        }

        .title-line {
          display: block;
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.8s ease, transform 0.8s ease;
        }

        .contact.in-view .title-line-1 {
          opacity: 1;
          transform: translateY(0);
          transition-delay: 0.2s;
        }

        .contact.in-view .title-line-2 {
          opacity: 1;
          transform: translateY(0);
          transition-delay: 0.4s;
        }

        .contact.in-view .title-line-3 {
          opacity: 1;
          transform: translateY(0);
          transition-delay: 0.6s;
        }

        .gradient-text {
          background: linear-gradient(135deg, #22d3ee 0%, #3b82f6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .section-description {
          font-size: 1.25rem;
          line-height: 1.7;
          color: #cbd5e1;
          max-width: 600px;
          margin: 0 auto;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.8s ease 0.8s, transform 0.8s ease 0.8s;
        }

        .contact.in-view .section-description {
          opacity: 1;
          transform: translateY(0);
        }

        /* Contact Grid */
        .contact-grid {
          display: grid;
          grid-template-columns: 1fr 1.2fr;
          gap: 60px;
          align-items: start;
        }

        /* Contact Information */
        .contact-info {
          display: flex;
          flex-direction: column;
          gap: 40px;
        }

        .info-section {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 24px;
          padding: 40px;
          backdrop-filter: blur(10px);
        }

        .info-title {
          font-size: 1.8rem;
          font-weight: 700;
          margin-bottom: 12px;
          color: #f8fafc;
        }

        .info-subtitle {
          font-size: 1rem;
          color: #94a3b8;
          margin-bottom: 32px;
        }

        .info-cards {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .info-card {
          display: flex;
          align-items: center;
          gap: 20px;
          padding: 24px;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 16px;
          transition: all 0.3s ease;
        }

        .info-card:hover {
          transform: translateY(-5px);
          border-color: rgba(34, 211, 238, 0.2);
          background: rgba(34, 211, 238, 0.05);
        }

        .card-icon {
          width: 60px;
          height: 60px;
          background: rgba(34, 211, 238, 0.1);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #22d3ee;
          font-size: 1.5rem;
          flex-shrink: 0;
          transition: transform 0.3s ease;
        }

        .info-card:hover .card-icon {
          transform: scale(1.1) rotate(5deg);
        }

        .card-content {
          flex: 1;
        }

        .card-content h4 {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 6px;
          color: #f8fafc;
        }

        .card-content p,
        .contact-link {
          font-size: 0.95rem;
          color: #cbd5e1;
          margin-bottom: 4px;
        }

        .contact-link {
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .contact-link:hover {
          color: #22d3ee;
        }

        .card-tag,
        .card-hint {
          font-size: 0.8rem;
          display: block;
          margin-top: 4px;
        }

        .card-tag {
          color: #22d3ee;
          background: rgba(34, 211, 238, 0.1);
          padding: 4px 12px;
          border-radius: 12px;
          display: inline-block;
        }

        .card-hint {
          color: #94a3b8;
        }

        /* Social Section */
        .social-section {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 24px;
          padding: 32px;
          backdrop-filter: blur(10px);
        }

        .social-section h4 {
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 20px;
          color: #f8fafc;
        }

        .social-links {
          display: flex;
          gap: 16px;
        }

        .social-link {
          width: 50px;
          height: 50px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #cbd5e1;
          font-size: 1.2rem;
          transition: all 0.3s ease;
        }

        .social-link:hover {
          background: rgba(34, 211, 238, 0.1);
          color: #22d3ee;
          transform: translateY(-3px);
        }

        /* Contact Form Section */
        .contact-form-section {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 24px;
          padding: 40px;
          backdrop-filter: blur(10px);
        }

        .form-header {
          margin-bottom: 40px;
        }

        .form-header h3 {
          font-size: 1.8rem;
          font-weight: 700;
          margin-bottom: 12px;
          color: #f8fafc;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .header-icon {
          color: #22d3ee;
        }

        .form-header p {
          font-size: 1rem;
          color: #94a3b8;
        }

        /* Contact Form */
        .contact-form {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-group label {
          font-size: 0.95rem;
          font-weight: 500;
          color: #cbd5e1;
        }

        .required {
          color: #ef4444;
        }

        .form-group input,
        .form-group textarea {
          padding: 16px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          color: #f8fafc;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .form-group input::placeholder,
        .form-group textarea::placeholder {
          color: #64748b;
        }

        .form-group input:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #22d3ee;
          background: rgba(34, 211, 238, 0.05);
          box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.1);
        }

        .form-group input.error,
        .form-group textarea.error {
          border-color: #ef4444;
        }

        .form-group input.error:focus,
        .form-group textarea.error:focus {
          border-color: #ef4444;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }

        .error-message {
          font-size: 0.85rem;
          color: #ef4444;
          margin-top: 4px;
        }

        .form-group textarea {
          resize: vertical;
          min-height: 120px;
        }

        .form-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 10px;
        }

        .form-note {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 0.9rem;
          color: #94a3b8;
        }

        .note-icon {
          color: #22d3ee;
        }

        .submit-button {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 18px 36px;
          background: linear-gradient(135deg, #22d3ee, #3b82f6);
          color: #020617;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 10px 30px rgba(34, 211, 238, 0.3);
        }

        .submit-button:hover:not(:disabled) {
          transform: translateY(-3px);
          box-shadow: 0 15px 40px rgba(34, 211, 238, 0.4);
          gap: 16px;
        }

        .submit-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .button-icon {
          transition: transform 0.3s ease;
        }

        .submit-button:hover:not(:disabled) .button-icon {
          transform: translateX(4px);
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: white;
          animation: spin 1s linear infinite;
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          animation: fadeIn 0.3s ease;
          backdrop-filter: blur(5px);
        }

        .modal-content {
          background: linear-gradient(135deg, #0f172a, #1e293b);
          padding: 48px;
          border-radius: 24px;
          text-align: center;
          max-width: 400px;
          width: 90%;
          border: 1px solid rgba(34, 211, 238, 0.2);
          animation: scaleIn 0.3s ease;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
        }

        .modal-icon {
          font-size: 4rem;
          margin-bottom: 24px;
        }

        .modal-icon.success {
          color: #22d3ee;
          animation: pulse 2s ease-in-out infinite;
        }

        .modal-icon.error {
          color: #ef4444;
        }

        .modal-content h3 {
          font-size: 1.8rem;
          font-weight: 700;
          margin-bottom: 16px;
          color: #f8fafc;
        }

        .modal-content p {
          font-size: 1.1rem;
          line-height: 1.6;
          color: #cbd5e1;
          margin-bottom: 32px;
        }

        .modal-button {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 16px 32px;
          background: rgba(34, 211, 238, 0.1);
          color: #22d3ee;
          border: 1px solid rgba(34, 211, 238, 0.3);
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          margin: 0 auto;
        }

        .modal-button:hover {
          background: rgba(34, 211, 238, 0.2);
          transform: translateY(-2px);
          gap: 14px;
        }

        /* Animations */
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.1); }
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .contact-grid {
            grid-template-columns: 1fr;
            gap: 40px;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .section-title {
            font-size: 3rem;
          }
        }

        @media (max-width: 768px) {
          .contact {
            padding: 100px 0;
          }

          .container {
            padding: 0 20px;
          }

          .section-title {
            font-size: 2.5rem;
          }

          .info-section,
          .contact-form-section,
          .social-section {
            padding: 30px;
          }

          .form-footer {
            flex-direction: column;
            gap: 20px;
            align-items: stretch;
          }

          .submit-button {
            width: 100%;
            justify-content: center;
          }

          .modal-content {
            padding: 32px 24px;
          }
        }

        @media (max-width: 480px) {
          .section-title {
            font-size: 2rem;
          }

          .info-card {
            flex-direction: column;
            text-align: center;
          }

          .card-icon {
            margin: 0 auto;
          }

          .social-links {
            justify-content: center;
          }
        }
      `}</style>
    </section>
  );
};

export default Contact;